/**
 * Daily Seeded Challenge (Phase 5). A deterministic challenge derived from the calendar date:
 * everyone playing on the same day faces the same modifiers, and you compete against your own
 * best score plus a completion streak. Framed as "the AI prepared today's gauntlet" so it
 * reinforces the rivalry hook instead of replacing it.
 *
 * Pure + Phaser-free so it is fully testable and shared between the menu, the battle scene, and
 * the reward loop. The modifiers ride the SAME battle hooks the War Funds shop already uses
 * (starting gold + difficulty pressure), so the battle scene barely changes.
 */
import type { DailyChallengeRecord } from "../../../shared/types/PlayerMemory";

export type DailyModifierId =
  | "iron_tide" // tougher enemies (pressure up)
  | "blitz" // faster onslaught (pressure up)
  | "warlord" // much tougher (pressure up a lot)
  | "famine" // less starting gold
  | "scorched" // much less starting gold (slight pressure too)
  | "warchest" // bonus starting gold (a gift)
  | "skirmish"; // lighter pressure (a gift)

export type DailyModifier = {
  id: DailyModifierId;
  /** Added to the battle's starting difficulty pressure (can be negative). */
  pressureDelta: number;
  /** Added to starting gold (can be negative). */
  goldDelta: number;
  /** Relative difficulty weight, used to scale the daily score (harder day = more points). */
  weight: number;
};

export const DAILY_MODIFIERS: Record<DailyModifierId, DailyModifier> = {
  iron_tide: { id: "iron_tide", pressureDelta: 0.22, goldDelta: 0, weight: 2 },
  blitz: { id: "blitz", pressureDelta: 0.16, goldDelta: 0, weight: 2 },
  warlord: { id: "warlord", pressureDelta: 0.34, goldDelta: 0, weight: 3 },
  famine: { id: "famine", pressureDelta: 0, goldDelta: -100, weight: 2 },
  scorched: { id: "scorched", pressureDelta: 0.08, goldDelta: -160, weight: 3 },
  warchest: { id: "warchest", pressureDelta: 0.05, goldDelta: 180, weight: 1 },
  skirmish: { id: "skirmish", pressureDelta: -0.12, goldDelta: 60, weight: 1 }
};

export const DAILY_MODIFIER_IDS = Object.keys(DAILY_MODIFIERS) as DailyModifierId[];

/** Base starting pressure for the daily mode, before the day's modifiers are folded in. */
export const DAILY_BASE_PRESSURE = 1.1;

/** How many distinct modifiers each day rolls. */
const DAILY_MODIFIER_COUNT = 2;

export type DailyChallenge = {
  dateKey: string;
  seed: number;
  modifiers: DailyModifier[];
  /** Summed pressure delta from the day's modifiers. */
  pressureDelta: number;
  /** Summed starting-gold delta from the day's modifiers. */
  goldDelta: number;
  /** Summed difficulty weight of the day (used to scale the score). */
  weight: number;
};

/** Local calendar date as YYYY-MM-DD (the daily challenge resets at local midnight). */
export function dateKeyOf(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Deterministic 32-bit seed from a date key string (FNV-1a). */
export function seedFromDateKey(key: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** mulberry32 PRNG: returns a function producing floats in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Build the daily challenge for a given seed (pure; used by both the date path and tests). */
export function dailyChallengeFromSeed(seed: number, dateKey = ""): DailyChallenge {
  const rng = mulberry32(seed);
  const pool = [...DAILY_MODIFIER_IDS];
  const picks: DailyModifier[] = [];
  for (let i = 0; i < DAILY_MODIFIER_COUNT && pool.length > 0; i += 1) {
    const idx = Math.floor(rng() * pool.length) % pool.length;
    const [id] = pool.splice(idx, 1);
    if (id) picks.push(DAILY_MODIFIERS[id]);
  }
  const pressureDelta = Number(picks.reduce((sum, m) => sum + m.pressureDelta, 0).toFixed(3));
  const goldDelta = picks.reduce((sum, m) => sum + m.goldDelta, 0);
  const weight = picks.reduce((sum, m) => sum + m.weight, 0);
  return { dateKey, seed, modifiers: picks, pressureDelta, goldDelta, weight };
}

/** The daily challenge for a calendar date. */
export function dailyChallengeForDate(date: Date): DailyChallenge {
  const key = dateKeyOf(date);
  return dailyChallengeFromSeed(seedFromDateKey(key), key);
}

/**
 * Score for a finished daily run. Winning earns a big base; survival time adds; the day's
 * difficulty weight scales a bonus so harder days are worth more. Fully deterministic.
 */
export function dailyScore(input: { win: boolean; battleTime: number; weight: number }): number {
  const survival = Math.min(600, Math.max(0, Math.round(input.battleTime)));
  const base = input.win ? 1000 : 0;
  const difficultyBonus = Math.round((input.win ? 120 : 40) * Math.max(0, input.weight));
  return base + survival * 2 + difficultyBonus;
}

/** True when `b` (YYYY-MM-DD) is exactly the calendar day after `a`. Pure (UTC arithmetic). */
export function isConsecutiveDay(a: string, b: string): boolean {
  const ams = Date.parse(`${a}T00:00:00Z`);
  const bms = Date.parse(`${b}T00:00:00Z`);
  if (Number.isNaN(ams) || Number.isNaN(bms)) return false;
  return bms - ams === 86400000;
}

/**
 * Fold a finished daily run into the persistent record. Pure: the caller supplies today's
 * date key so the streak/reset logic is testable. Best score is per-day; the streak counts
 * consecutive distinct days whose challenge was won at least once.
 */
export function nextDailyRecord(
  previous: DailyChallengeRecord | undefined,
  today: { dateKey: string; score: number; win: boolean }
): DailyChallengeRecord {
  const isNewDay = !previous || previous.dateKey !== today.dateKey;
  const bestScore = isNewDay ? today.score : Math.max(previous!.bestScore, today.score);
  const wasCompletedToday = isNewDay ? false : previous!.completed;
  const completed = wasCompletedToday || today.win;

  let streak = previous?.streak ?? 0;
  let lastCompletedDate = previous?.lastCompletedDate;
  // Only the FIRST win of a given day moves the streak.
  if (today.win && !wasCompletedToday) {
    streak = lastCompletedDate && isConsecutiveDay(lastCompletedDate, today.dateKey) ? streak + 1 : 1;
    lastCompletedDate = today.dateKey;
  }
  return { dateKey: today.dateKey, bestScore, completed, streak, lastCompletedDate };
}
